import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Tag, message, Space } from 'antd';
import { CheckOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { taskAPI, contentAPI } from '../services/api';

const TaskDetail = () => {
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // 获取任务详情
  const fetchTaskDetail = useCallback(async () => {
    setLoading(true);
    try {
      // 获取任务详情
      const taskResponse = await taskAPI.getTask(id);
      setTask(taskResponse.data);

      // 获取任务分配
      const assignmentsResponse = await taskAPI.getTaskAssignments(id);
      setAssignments(assignmentsResponse.data);

      // 暂时简单设置当前用户ID
      setCurrentUser(1); // 实际应用中应该从登录状态中获取
    } catch (error) {
      console.error('获取任务详情错误:', error);
      message.error('获取任务详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTaskDetail();
  }, [fetchTaskDetail]);



  // 处理保存内容
  const handleSaveContent = async () => {
    if (!content) {
      message.error('内容不能为空');
      return;
    }

    // 查找当前用户的任务分配
    const assignment = assignments.find(a => a.user_id === currentUser);
    if (!assignment) {
      message.error('您没有分配此任务');
      return;
    }

    try {
      await contentAPI.createContent({
        task_assignment_id: assignment.id,
        content: content,
        created_by: currentUser
      });
      message.success('内容保存成功');
    } catch (error) {
      console.error('保存内容错误:', error);
      message.error('保存内容失败');
    }
  };

  // 处理完成任务
  const handleCompleteTask = async () => {
    try {
      await taskAPI.completeTask(id, {
        user_id: currentUser
      });
      message.success('任务完成成功');
      // 刷新任务详情
      fetchTaskDetail();
    } catch (error) {
      console.error('完成任务错误:', error);
      message.error('完成任务失败');
    }
  };

  if (loading) {
    return <div>加载中...</div>;
  }

  if (!task) {
    return <div>任务不存在</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="glass-page-title">{task.name}</h1>
        <p className="glass-page-subtitle">{task.description}</p>
      </div>

      {/* 任务基本信息 */}
      <Card className="glass-card" title="任务基本信息" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="large">
          <Space>
            <span>状态：</span>
            <Tag color={task.status === 'completed' ? 'green' : 'blue'}>
              {task.status === 'completed' ? '已完成' : '进行中'}
            </Tag>
          </Space>
          <Space>
            <span>任务类型：</span>
            <Tag color="purple">
              {task.type === 'rich_text' ? '富文本填写' : '操作执行'}
            </Tag>
          </Space>
          <Space>
            <span>创建时间：</span>
            <span>{new Date(task.created_at).toLocaleString()}</span>
          </Space>
          {task.completed_at && (
            <Space>
              <span>完成时间：</span>
              <span>{new Date(task.completed_at).toLocaleString()}</span>
            </Space>
          )}
        </Space>
      </Card>

      {/* 任务分配 */}
      <Card className="glass-card" title="任务分配" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="large">
          {assignments.map(assignment => (
            <Space key={assignment.id} style={{ width: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <UserOutlined style={{ marginRight: 8 }} />
                <span>用户ID: {assignment.user_id}</span>
              </div>
              <Tag color={assignment.status === 'completed' ? 'green' : 'blue'}>
                {assignment.status === 'completed' ? '已完成' : '进行中'}
              </Tag>
            </Space>
          ))}
        </Space>
      </Card>

      {/* 文本编辑器（如果是富文本任务） */}
      {task.type === 'rich_text' && (
        <Card className="glass-card" title="发版内容" style={{ marginBottom: 24 }}>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="glass-input"
            style={{
              width: '100%',
              minHeight: 500,
              padding: 12,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: 8,
              resize: 'vertical',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#fff'
            }}
            placeholder="请输入发版内容..."
          />
          <div className="glass-form" style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              onClick={handleSaveContent}
              style={{ marginRight: 8 }}
            >
              保存内容
            </Button>
            <Button 
              type="primary" 
              icon={<CheckOutlined />} 
              onClick={handleCompleteTask}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              完成任务
            </Button>
          </div>
        </Card>
      )}

      {/* 操作执行任务 */}
      {task.type === 'operation' && (
        <Card className="glass-card" title="操作执行" style={{ marginBottom: 24 }}>
          <div style={{ textAlign: 'center', padding: 48, color: 'rgba(255, 255, 255, 0.5)' }}>
            <p>此任务需要您执行相关操作后标记完成</p>
            <Button 
              type="primary" 
              icon={<CheckOutlined />} 
              onClick={handleCompleteTask}
              size="large"
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              标记完成
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default TaskDetail;
